import { Helmet } from "react-helmet-async";

const SITE_NAME = "Wet Cabbage";
const SITE_URL = "https://wetcabbage.com";
const DEFAULT_DESCRIPTION =
  "Personalised streetwear for people who've eaten a kebab in a car park with dignity. Six divisions documenting modern leisure behaviour. Supporting the underdressed and overlooked.";
const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_chaotic-threads/artifacts/2j36xdpo_18A59D19-9E21-430B-B766-CD44A58547F2.png";
const TWITTER_HANDLE = "@wetcabbage";

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path = "",
  type = "website",
  schema,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Premium Streetwear for Modern Leisure`;
  const url = path ? `${SITE_URL}${path}` : SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

// Sitewide Organization + WebSite schema (rendered once in Layout)
export const SITEWIDE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
      description: DEFAULT_DESCRIPTION,
      sameAs: [
        "https://instagram.com/wetcabbage",
        "https://tiktok.com/@wetcabbage",
        "https://twitter.com/wetcabbage",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
  ],
};

export const productSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.description,
  image: product.image,
  brand: { "@type": "Brand", name: SITE_NAME },
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/shop/${product.id}`,
    priceCurrency: product.currency || "USD",
    price: product.price.toFixed(2),
    availability: "https://schema.org/InStock",
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
